import { useCallback } from "react";
import type { MessageEvent } from "@veto/domain";
import {
  addMessageIfAbsent,
  listMessages as listMessagesRaw,
  putMessage as putMessageRaw,
} from "@veto/storage";
import { decryptUtf8, encryptUtf8 } from "../vaultCrypto";

export function useEncryptedMessages(vaultKeyB64: string | null) {
  const putMessage = useCallback(
    async (msg: MessageEvent) => {
      if (!vaultKeyB64) {
        await putMessageRaw(msg);
        return;
      }
      const encBody = await encryptUtf8(vaultKeyB64, msg.body);
      await putMessageRaw({ ...msg, body: `enc:v1:${encBody}` });
    },
    [vaultKeyB64],
  );

  const listMessages = useCallback(
    async (conversationId: string) => {
      const rows = await listMessagesRaw(conversationId);
      if (!vaultKeyB64) return rows;
      return Promise.all(
        rows.map(async (m) => {
          if (!m.body.startsWith("enc:v1:")) return m;
          try {
            const body = await decryptUtf8(vaultKeyB64, m.body.slice("enc:v1:".length));
            return { ...m, body };
          } catch {
            return { ...m, body: "[unable to decrypt local copy]" };
          }
        }),
      );
    },
    [vaultKeyB64],
  );

  /** Inbound insert: never replaces an existing row (review L2). */
  const insertMessage = useCallback(
    async (msg: MessageEvent) => {
      if (!vaultKeyB64) return addMessageIfAbsent(msg);
      const encBody = await encryptUtf8(vaultKeyB64, msg.body);
      return addMessageIfAbsent({ ...msg, body: `enc:v1:${encBody}` });
    },
    [vaultKeyB64],
  );

  return { putMessage, insertMessage, listMessages };
}
